import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Container,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import FavoriteIcon from '@mui/icons-material/Favorite';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { NavLink } from 'react-router-dom';

const Navbar = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElPrograms, setAnchorElPrograms] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const aboutMenuItems = [
    { text: 'Who We Are', path: '/about/history' },
    { text: 'Vision, Mission & Values', path: '/about/vision' },
    { text: 'Core Functions of the Network', path: '/about/core-functions' },
    { text: 'Organizational Objectives & Engagement Areas', path: '/about/objectives' },
    { text: 'Governance Structure, Members & Membership Criteria', path: '/about/governance' },
  ];

  const programsMenuItems = [
    { text: 'Promoting enabling environment', path: '/programs/Promoting-Enabling-Environment' },
    { text: 'Development partnerships & Supporting marginalized Groups', path: '/programs/development-partnerships-marginalized-groups' },
    { text: 'Peace Promotion & Supporting Democratization', path: '/programs/Peace-Promotion-Democratization' },
    { text: 'Good Governance & Ethical Practices', path: '/programs/good-governance-and-ethics' },
    { text: 'Coordination and Support & Global Networking', path: '/programs/coordination-and-networking' },
    { text: 'Research & Studies', path: '/programs/research-studies' },
    { text: 'Major Activities Accomplished over the Last Three Years', path: '/programs/major-activities' },
  ];

  const menuItems = [
    { text: 'Home', path: '/' },
    { text: 'Gallery', path: '/gallery' },
    { text: 'Members', path: '/members' },
    { text: 'News', path: '/blog' },
    { text: 'Contact', path: '/contact' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleAboutMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAboutMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProgramsMenuOpen = (event) => {
    setAnchorElPrograms(event.currentTarget);
  };

  const handleProgramsMenuClose = () => {
    setAnchorElPrograms(null);
  };

  const DonateButton = () => (
    <Button
      variant="contained"
      color="secondary"
      startIcon={<FavoriteIcon />}
      component={NavLink}
      to="/donate"
      sx={{
        borderRadius: '20px',
        px: 3,
        py: 1,
        textTransform: 'none',
        fontWeight: 600,
        backgroundColor: '#e74c3c',
        '&:hover': {
          backgroundColor: '#c0392b',
          transform: 'translateY(-2px)',
        },
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      }}
    >
      Donate
    </Button>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: { xs: '64px', md: '70px' }, py: { xs: 0.5, md: 0.75 } }}>
          {/* Logo */}
          <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', mr: { xs: 1, md: 3 } }}>
            <NavLink to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <img
                src="/logo-necsoo.png"
                alt="NeCSOO Logo"
                style={{ height: '50px', width: 'auto', marginRight: '10px', transition: 'transform 0.3s ease' }}
              />
              <Box
                component="span"
                sx={{
                  fontSize: { xs: '1.5rem', md: '1.75rem' },
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  display: { xs: 'none', md: 'block' },
                  letterSpacing: '0.5px',
                }}
              >
                NeCSOO
              </Box>
            </NavLink>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
              {/* Home */}
              <Button
                component={NavLink}
                to="/"
                sx={{
                  color: 'text.primary',
                  px: 1.5,
                  '&.active': {
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  },
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              >
                Home
              </Button>

              {/* About Menu */}
              <Button
                endIcon={<KeyboardArrowDownIcon />}
                onClick={handleAboutMenuOpen}
                sx={{
                  color: 'text.primary',
                  px: 1.5,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              >
                About
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleAboutMenuClose}
                MenuListProps={{ 'aria-labelledby': 'about-button' }}
                sx={{
                  '& .MuiPaper-root': {
                    mt: 1,
                    minWidth: 220,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  },
                }}
              >
                {aboutMenuItems.map((item) => (
                  <MenuItem
                    key={item.text}
                    component={NavLink}
                    to={item.path}
                    onClick={handleAboutMenuClose}
                    sx={{
                      py: 1,
                      '&.active': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    {item.text}
                  </MenuItem>
                ))}
              </Menu>

              {/* Programs - Hover Based */}
              <Box onMouseEnter={handleProgramsMenuOpen} onMouseLeave={handleProgramsMenuClose}>
                <Button
                  sx={{
                    color: 'text.primary',
                    px: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                  endIcon={<KeyboardArrowDownIcon fontSize="small" />}
                >
                  Programs
                </Button>
                <Menu
                  anchorEl={anchorElPrograms}
                  open={Boolean(anchorElPrograms)}
                  onClose={handleProgramsMenuClose}
                  MenuListProps={{ onMouseLeave: handleProgramsMenuClose }}
                  sx={{
                    '& .MuiPaper-root': {
                      mt: 1,
                      minWidth: 280,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  {programsMenuItems.map((item) => (
                    <MenuItem
                      key={item.text}
                      component={NavLink}
                      to={item.path}
                      onClick={handleProgramsMenuClose}
                      sx={{
                        py: 1,
                        whiteSpace: 'normal',
                        '&.active': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {item.text}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>

              {/* Other Links */}
              {menuItems.slice(1).map((item) => (
                <Button
                  key={item.text}
                  component={NavLink}
                  to={item.path}
                  sx={{
                    color: 'text.primary',
                    px: 1.5,
                    '&.active': {
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          )}

          {/* Donate Button */}
          {!isMobile && (
  <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
    <DonateButton />
    <Button
      variant="outlined"
      color="primary"
      onClick={() => navigate('/login')}
      sx={{
        borderRadius: '20px',
        px: 3,
        py: 1,
        textTransform: 'none',
        fontWeight: 600,
        '&:hover': {
          backgroundColor: 'rgba(0,0,0,0.04)',
          transform: 'translateY(-1px)',
        },
        transition: 'all 0.3s ease',
      }}
    >
      Login
    </Button>
  </Box>
)}


          {/* Mobile Menu Icon */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleDrawerToggle}
              sx={{ ml: 'auto', color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ '& .MuiDrawer-paper': { width: 260, pt: 1 } }}
      >
        <Box sx={{ width: 260 }} role="presentation">
          <List>
            <ListItem
              button
              component={NavLink}
              to="/"
              onClick={handleDrawerToggle}
              sx={{
                py: 1,
                '&.active': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  '& .MuiListItemText-primary': {
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  },
                },
              }}
            >
              <ListItemText primary="Home" />
            </ListItem>

            {/* About Section (Collapsible) */}
            <ListItem
              button
              onClick={() => setAboutOpen(!aboutOpen)}
              sx={{ py: 1, '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
            >
              <ListItemText
                primary="About"
                sx={{
                  '& .MuiTypography-root': {
                    color: aboutOpen ? theme.palette.primary.main : 'inherit',
                    fontWeight: aboutOpen ? 600 : 'inherit',
                  },
                }}
              />
              {aboutOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={aboutOpen} timeout="auto" unmountOnClose>
              <List component="div" disablePadding>
                {aboutMenuItems.map((item) => (
                  <ListItem
                    button
                    component={NavLink}
                    to={item.path}
                    key={item.text}
                    onClick={handleDrawerToggle}
                    sx={{
                      pl: 3,
                      py: 0.75,
                      '&.active': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        '& .MuiListItemText-primary': {
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        },
                      },
                    }}
                  >
                    <ListItemText primary={item.text} />
                  </ListItem>
                ))}
              </List>
            </Collapse>

            {/* Programs section with collapse */}
<ListItem
  button
  onClick={() => setProgramsOpen(!programsOpen)}
  sx={{
    py: 1,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  }}
>
  <ListItemText
    primary="Programs"
    sx={{
      '& .MuiTypography-root': {
        color: programsOpen ? theme.palette.primary.main : 'inherit',
        fontWeight: programsOpen ? 600 : 'inherit',
      },
    }}
  />
  {programsOpen ? <ExpandLess /> : <ExpandMore />}
</ListItem>
<Collapse in={programsOpen} timeout="auto" unmountOnClose>
  <List component="div" disablePadding>
    {programsMenuItems.map((item) => (
      <ListItem
        button
        component={NavLink}
        to={item.path}
        key={item.text}
        onClick={handleDrawerToggle}
        sx={{
          pl: 3,
          py: 0.75,
          '&.active': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '& .MuiListItemText-primary': {
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
          },
        }}
      >
        <ListItemText
          primary={item.text}
          primaryTypographyProps={{
            style: {
              fontSize: '0.9rem',
              fontWeight: 'inherit',
              whiteSpace: 'normal',
            },
          }}
        />
      </ListItem>
    ))}
  </List>
</Collapse>


            {/* Other Links */}
            {menuItems.slice(1).map((item) => (
              <ListItem
                button
                component={NavLink}
                to={item.path}
                key={item.text}
                onClick={handleDrawerToggle}
                sx={{
                  py: 1,
                  '&.active': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    '& .MuiListItemText-primary': {
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <ListItemText primary={item.text} />
              </ListItem>
            ))}

            <ListItem sx={{ justifyContent: 'center', mt: 1, mb: 1 }}>
              <DonateButton />
            </ListItem>
            <ListItem sx={{ justifyContent: 'center', mb: 2 }}>
  <Button
    variant="outlined"
    color="primary"
    onClick={() => navigate('/login')}
    sx={{
      borderRadius: '20px',
      px: 3,
      py: 1,
      textTransform: 'none',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: 'rgba(0,0,0,0.04)',
        transform: 'translateY(-1px)',
      },
      transition: 'all 0.3s ease',
    }}
  >
  Login
  </Button>
</ListItem>

          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
